import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { User } from "@iventis/domain-model/model/user";
import { UserGroup } from "@iventis/domain-model/model/userGroup";
import { UserGroupWithUsers } from "@iventis/domain-model/model/userGroupWithUsers";
import { createMachine, assign, DoneInvokeEvent } from "xstate";
import { Content } from "@iventis/translations/content/typed-content";
import { Permission } from "@iventis/domain-model/model/permission";
import { PermissionOwnerType } from "@iventis/domain-model/model/permissionOwnerType";
import { PermissionResourceType } from "@iventis/domain-model/model/permissionResourceType";
import {
    AddPeopleToTeamEvent,
    CollapseTeamEvent,
    ExpandTeamEvent,
    RequestUpdateTeamLeaderEvent,
    RemovePersonFromTeamEvent,
    SetPersonForTeamRemovalEvent,
    SaveCreateTeamEvent,
    TeamSelectedForPersonAdditionEvent,
    TeamsViewMachineContext,
    TeamsViewMachineEvent,
    TeamsViewMachineEvents,
    UpdateTeamLeaderEvent,
    DeleteTeamEvent,
} from "./teams-view.types";
import { TeamApiCallbacks } from "./types";

const actions = {
    updatingTeamLeaderDone: [
        assign((context: TeamsViewMachineContext, event: DoneInvokeEvent<UpdateTeamLeaderEvent>) => ({
            ...context,
            teams: updateContextForTeamLeader(context.teams, event.data.payload.teamId, event.data.payload.userId, event.data.payload.authorisation),
            personToUpdateLeader: undefined,
        })),
        ({ toast, translate }) =>
            toast.success(
                {
                    title: translate(Content.common.success),
                    message: translate(Content.people.toasts.setTeamLeaderSuccess),
                },
                { autoHideDuration: 5000 }
            ),
    ],
    removePersonFromTeamDone: [
        assign((context: TeamsViewMachineContext, event: DoneInvokeEvent<RemovePersonFromTeamEvent>) => ({
            ...context,
            personRemovalFromTeamSelection: undefined,
            teams: context.teams.map((team) =>
                team.id === event.data.payload.team.id ? { ...team, userMembers: team.userMembers.filter((person) => person.id !== event.data.payload.person.id) } : team
            ),
        })),
        ({ toast, translate }) =>
            toast.success(
                {
                    title: translate(Content.common.success),
                    message: translate(Content.people.toasts.removeFromTeamSuccess),
                },
                { autoHideDuration: 5000 }
            ),
    ],
    teamsLoadingDone: [
        assign((context: TeamsViewMachineContext, event: DoneInvokeEvent<UserGroupWithUsers[]>) => ({
            ...context,
            teams: event.data,
            expandedTeams: event.data.map((group) => group.id),
        })),
    ],
    addingPeopleToTeamDone: [
        assign((context: TeamsViewMachineContext, event: DoneInvokeEvent<AddPeopleToTeamEvent>) => ({
            ...context,
            teams: addPeopleToTeamContext(context.teams, event.data.payload.people, event.data.payload.teamId),
            teamForPersonAddition: undefined,
        })),
    ],
    saveNewTeamDone: [
        assign({
            teams: (context: TeamsViewMachineContext, event: DoneInvokeEvent<UserGroup>) => [...context.teams, { ...event.data, userMembers: [], permissions: [] }],
            expandedTeams: (context: TeamsViewMachineContext, event: DoneInvokeEvent<UserGroup>) => [...context.expandedTeams, event.data.id],
        }),
        ({ toast, translate }) =>
            toast.success(
                {
                    title: translate(Content.common.success),
                    message: translate(Content.people.toasts.add_team_success),
                },
                { autoHideDuration: 5000 }
            ),
    ],
    deleteTeamDone: [
        assign({
            teams: (context: TeamsViewMachineContext, event: DoneInvokeEvent<string>) =>
                context.teams.reduce((cum, value) => (value.id !== event.data ? [...cum, value] : [...cum]), []),
        }),
        ({ toast, translate }) => {
            toast.success(
                {
                    title: translate(Content.common.success),
                    message: translate(Content.people.toasts.deleteTeamSuccess),
                },
                { autoHideDuration: 5000 }
            );
        },
    ],
};

export const teamsViewMachine = ({ getTeams, removePersonFromTeam, createTeam, deleteTeam, updateTeamLeader, addPeopleToTeam }: TeamApiCallbacks) =>
    createMachine<TeamsViewMachineContext, TeamsViewMachineEvents>({
        id: "teams-view-machine",
        context: {
            teams: undefined,
            teamForPersonAddition: undefined,
            expandedTeams: [],
            personToUpdateLeader: undefined,
            personRemovalFromTeamSelection: undefined,
            teamToDelete: undefined,
            translate: undefined,
            toast: undefined,
        },
        type: "parallel",
        states: {
            teamLeader: {
                initial: "idle",
                states: {
                    idle: {
                        on: {
                            [TeamsViewMachineEvent.REQUEST_UPDATE_TEAM_LEADER]: {
                                target: "#updateTeamLeaderRequested",
                            },
                        },
                    },
                    updatingTeamLeader: {
                        id: "updatingTeamLeader",
                        invoke: {
                            src: async (_, event: UpdateTeamLeaderEvent) => {
                                await updateTeamLeader(event.payload.teamId, event.payload.userId, event.payload.authorisation);
                                return event;
                            },
                            onDone: {
                                actions: actions.updatingTeamLeaderDone,
                                target: "idle",
                            },
                            onError: {
                                target: "updateTeamLeaderRequested",
                            },
                        },
                    },
                    updateTeamLeaderRequested: {
                        id: "updateTeamLeaderRequested",
                        entry: assign((context, event: RequestUpdateTeamLeaderEvent) => ({
                            ...context,
                            personToUpdateLeader: event.payload,
                        })),
                        on: {
                            [TeamsViewMachineEvent.UPDATE_TEAM_LEADER]: {
                                target: "#updatingTeamLeader",
                            },
                            [TeamsViewMachineEvent.CANCEL_TEAM_LEADER]: {
                                target: "idle",
                            },
                        },
                    },
                },
            },
            removeFromTeam: {
                initial: "idle",
                on: {
                    [TeamsViewMachineEvent.SET_PERSON_FOR_TEAM_REMOVAL]: {
                        actions: assign((context, event: SetPersonForTeamRemovalEvent) => ({ ...context, personRemovalFromTeamSelection: event.payload })),
                    },
                },
                states: {
                    idle: {
                        on: {
                            [TeamsViewMachineEvent.REMOVE_PERSON_FROM_TEAM]: {
                                target: "removingPersonFromTeam",
                            },
                        },
                    },
                    removingPersonFromTeam: {
                        invoke: {
                            src: async (_, event: RemovePersonFromTeamEvent) => {
                                await removePersonFromTeam(event.payload.team.id, event.payload.person.id);
                                return event;
                            },
                            onDone: {
                                actions: actions.removePersonFromTeamDone,
                                target: "idle",
                            },
                            onError: {
                                target: "idle",
                            },
                        },
                    },
                },
            },
            teamsList: {
                initial: "teamsLoading",
                states: {
                    teamsLoading: {
                        id: "teamsLoading",
                        invoke: {
                            src: getTeams,
                            onDone: {
                                actions: actions.teamsLoadingDone,
                                target: "idle",
                            },
                            onError: "teamsLoadingError",
                        },
                    },
                    teamsLoadingError: {
                        id: "teamsLoadingError",
                        type: "final",
                    },
                    addingPeopleToTeam: {
                        id: "addingPeopleToTeam",
                        invoke: {
                            src: async (_, event: AddPeopleToTeamEvent) => {
                                await addPeopleToTeam(
                                    event.payload.teamId,
                                    event.payload.people.map((person) => person.id)
                                );
                                return event;
                            },
                            onDone: {
                                actions: actions.addingPeopleToTeamDone,
                                target: "#idle",
                            },
                            onError: {
                                // Error displayed via axios interceptor
                                target: "#idle",
                            },
                        },
                    },
                    createTeam: {
                        id: "createTeam",
                        initial: "addTeamData",
                        states: {
                            addTeamData: {
                                id: "addTeamData",
                                on: {
                                    [TeamsViewMachineEvent.CANCEL_CREATE_TEAM]: {
                                        target: "#idle",
                                    },
                                    [TeamsViewMachineEvent.SAVE_NEW_TEAM]: {
                                        target: "saveNewTeam",
                                    },
                                },
                            },
                            saveNewTeam: {
                                id: "saveNewTeam",
                                invoke: {
                                    src: (_, event: SaveCreateTeamEvent) => createTeam(event.payload),
                                    onDone: {
                                        actions: actions.saveNewTeamDone,
                                        target: "#idle",
                                    },
                                    onError: {
                                        target: "#idle",
                                    },
                                },
                            },
                        },
                    },
                    deleteTeam: {
                        id: "deleteTeam",
                        initial: "confirmationDialog",
                        entry: assign((context, event: DeleteTeamEvent) => ({
                            ...context,
                            teamToDelete: event.payload.team,
                        })),
                        states: {
                            confirmationDialog: {
                                id: "deleteTeamConfirmationDialog",
                                on: {
                                    [TeamsViewMachineEvent.CANCEL_DELETE_TEAM]: {
                                        target: "#idle",
                                    },
                                    [TeamsViewMachineEvent.CONFIRM_DELETE_TEAM]: {
                                        target: "deleteSelectedTeam",
                                    },
                                },
                            },
                            deleteSelectedTeam: {
                                id: "deleteSelectedTeam",
                                invoke: {
                                    src: (context) => deleteTeam(context.teamToDelete.id),
                                    onDone: {
                                        actions: actions.deleteTeamDone,
                                        target: "#idle",
                                    },
                                    onError: {
                                        target: "#idle",
                                    },
                                },
                            },
                        },
                    },
                    idle: {
                        id: "idle",
                        on: {
                            [TeamsViewMachineEvent.EXPAND]: {
                                actions: assign((context, event: ExpandTeamEvent) => ({ ...context, expandedTeams: [...context.expandedTeams, event.payload] })),
                            },
                            [TeamsViewMachineEvent.COLLAPSE]: {
                                actions: assign((context, event: CollapseTeamEvent) => ({ ...context, expandedTeams: context.expandedTeams.filter((id) => id !== event.payload) })),
                            },
                            [TeamsViewMachineEvent.ADD_PEOPLE_TO_TEAM]: {
                                target: "addingPeopleToTeam",
                            },
                            [TeamsViewMachineEvent.TEAM_SELECTED_FOR_PERSON_ADDITION]: {
                                actions: assign((context, event: TeamSelectedForPersonAdditionEvent) => ({ ...context, teamForPersonAddition: event.payload.team })),
                            },
                            [TeamsViewMachineEvent.CREATE_TEAM]: {
                                target: "createTeam",
                            },
                            [TeamsViewMachineEvent.DELETE_TEAM]: {
                                target: "deleteTeam",
                            },
                        },
                    },
                },
            },
        },
    });

function addPeopleToTeamContext(teams: UserGroupWithUsers[], peopleToAdd: User[], teamId: string): UserGroupWithUsers[] {
    const teamInQuestion = teams.find((team) => team.id === teamId);
    return [
        ...teams.filter((team) => team.id !== teamId),
        {
            ...teamInQuestion,
            userMembers: [...teamInQuestion.userMembers, ...peopleToAdd],
            permissions: [...teamInQuestion.permissions, ...createNewMemberPermissions(peopleToAdd, teamInQuestion.id)],
        },
    ];
}

function createNewMemberPermissions(peopleToAdd: User[], teamId: string): Permission[] {
    return peopleToAdd.map((person) => ({
        id: null,
        ownerType: PermissionOwnerType.User,
        ownerId: person.id,
        ownerName: person.lastName,
        resourceType: PermissionResourceType.Group,
        resourceId: teamId,
        authorisation: Authorisation.Write,
        inheritable: true,
    }));
}

function updateContextForTeamLeader(teams: UserGroupWithUsers[], teamId: string, userIdToUpdate: string, authorisation: Authorisation): UserGroupWithUsers[] {
    return teams.map((x) =>
        x.id === teamId
            ? {
                  ...x,
                  permissions: x.permissions.map((y) => (y.ownerId === userIdToUpdate ? { ...y, authorisation } : y)),
              }
            : x
    );
}
